import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2,
  Calendar, RefreshCw, Download, BarChart3, LineChart, Target
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplianceTrendsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [trendsData, setTrendsData] = useState(null);
  const [selectedDays, setSelectedDays] = useState('90');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchTrendsData();
  }, [selectedDays]);

  const fetchTrendsData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/grc/trends/historical`, {
        params: { days: parseInt(selectedDays) },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTrendsData(response.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Failed to load compliance trends');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordSnapshot = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API_URL}/api/grc/trends/snapshot`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Compliance snapshot recorded');
      fetchTrendsData();
    } catch (error) {
      toast.error('Failed to record snapshot');
    } finally {
      setRefreshing(false);
    }
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getTrendColor = (change) => {
    if (change > 0) return 'text-green-500';
    if (change < 0) return 'text-red-500';
    return 'text-slate-500';
  };

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#fff',
        bodyColor: '#e2e8f0',
        borderColor: '#475569',
        borderWidth: 1,
        padding: 12,
        displayColors: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxTicksLimit: 10,
          color: '#94a3b8'
        }
      },
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(148, 163, 184, 0.1)'
        },
        ticks: {
          callback: (value) => `${value}%`,
          color: '#94a3b8'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const overallTrendData = trendsData ? {
    labels: trendsData.trend_data.map(d => d.date),
    datasets: [
      {
        label: 'Overall Compliance',
        data: trendsData.trend_data.map(d => d.score),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgb(59, 130, 246)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2
      }
    ]
  } : null;

  const frameworkBarData = trendsData ? {
    labels: trendsData.framework_trends.map(f => f.framework_code),
    datasets: [
      {
        label: 'Current Score',
        data: trendsData.framework_trends.map(f => f.current_score),
        backgroundColor: trendsData.framework_trends.map(f => 
          f.current_score >= 70 ? 'rgba(34, 197, 94, 0.8)' :
          f.current_score >= 50 ? 'rgba(234, 179, 8, 0.8)' :
          'rgba(239, 68, 68, 0.8)'
        ),
        borderRadius: 4
      }
    ]
  } : null;

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleColor: '#fff',
        bodyColor: '#e2e8f0',
        padding: 12,
        callbacks: {
          label: (context) => `Score: ${context.raw.toFixed(1)}%`
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#94a3b8'
        }
      },
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(148, 163, 184, 0.1)'
        },
        ticks: {
          callback: (value) => `${value}%`,
          color: '#94a3b8'
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="compliance-trends-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Compliance Trends
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track your compliance posture over time
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDays} onValueChange={setSelectedDays}>
            <SelectTrigger className="w-36" data-testid="days-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 180 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleRecordSnapshot}
            disabled={refreshing}
            data-testid="record-snapshot-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Record Snapshot
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="overall-score-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall Score</p>
                <p className="text-3xl font-bold mt-1">
                  {trendsData?.overall_score.toFixed(1)}%
                </p>
              </div>
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Target className="w-7 h-7 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="change-30d-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">30-Day Change</p>
                <div className="flex items-center gap-2 mt-1">
                  {getTrendIcon(trendsData?.overall_change_30d)}
                  <span className={`text-3xl font-bold ${getTrendColor(trendsData?.overall_change_30d)}`}>
                    {trendsData?.overall_change_30d > 0 ? '+' : ''}{trendsData?.overall_change_30d.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="change-90d-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">90-Day Change</p>
                <div className="flex items-center gap-2 mt-1">
                  {getTrendIcon(trendsData?.overall_change_90d)}
                  <span className={`text-3xl font-bold ${getTrendColor(trendsData?.overall_change_90d)}`}>
                    {trendsData?.overall_change_90d > 0 ? '+' : ''}{trendsData?.overall_change_90d.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="frameworks-tracked-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Frameworks Tracked</p>
                <p className="text-3xl font-bold mt-1">
                  {trendsData?.framework_trends.length || 0}
                </p>
              </div>
              <div className="w-14 h-14 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                <LineChart className="w-7 h-7 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="frameworks" data-testid="tab-frameworks">By Framework</TabsTrigger>
          <TabsTrigger value="recommendations" data-testid="tab-recommendations">Recommendations</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Overall Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Overall Compliance Trend</CardTitle>
              <CardDescription>
                Your aggregate compliance score over the selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80" data-testid="overall-trend-chart">
                {overallTrendData && <Line data={overallTrendData} options={lineChartOptions} />}
              </div>
            </CardContent>
          </Card>

          {/* Framework Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Framework Comparison</CardTitle>
              <CardDescription>
                Current compliance scores across all tracked frameworks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64" data-testid="framework-comparison-chart">
                {frameworkBarData && <Bar data={frameworkBarData} options={barChartOptions} />}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="frameworks" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trendsData?.framework_trends.map((fw, index) => (
              <Card key={fw.framework_code} data-testid={`framework-card-${fw.framework_code}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{fw.framework_name}</CardTitle>
                    <Badge variant={fw.current_score >= 70 ? 'default' : fw.current_score >= 50 ? 'secondary' : 'destructive'}>
                      {fw.current_score.toFixed(1)}%
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Progress value={fw.current_score} className="h-2 mb-4" />
                  <div className="flex justify-between text-sm">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">30d:</span>
                      <span className={getTrendColor(fw.change_30d)}>
                        {fw.change_30d > 0 ? '+' : ''}{fw.change_30d.toFixed(1)}%
                      </span>
                      {getTrendIcon(fw.change_30d)}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">90d:</span>
                      <span className={getTrendColor(fw.change_90d)}>
                        {fw.change_90d > 0 ? '+' : ''}{fw.change_90d.toFixed(1)}%
                      </span>
                      {getTrendIcon(fw.change_90d)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="recommendations" className="space-y-4">
          {/* Risk Areas */}
          {trendsData?.risk_areas?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Areas Needing Attention
                </CardTitle>
                <CardDescription>
                  Frameworks with compliance scores below target
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trendsData.risk_areas.map((area, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                      data-testid={`risk-area-${index}`}
                    >
                      <div>
                        <p className="font-medium">{area.framework}</p>
                        <p className="text-sm text-muted-foreground">{area.recommendation}</p>
                      </div>
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        {area.score.toFixed(1)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Improvement Areas */}
          {trendsData?.improvement_areas?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Positive Momentum
                </CardTitle>
                <CardDescription>
                  Frameworks showing improvement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trendsData.improvement_areas.map((area, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                      data-testid={`improvement-area-${index}`}
                    >
                      <div>
                        <p className="font-medium">{area.framework}</p>
                        <p className="text-sm text-muted-foreground">{area.message}</p>
                      </div>
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                        +{area.improvement.toFixed(1)}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No recommendations */}
          {(!trendsData?.risk_areas?.length && !trendsData?.improvement_areas?.length) && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-lg font-medium">Looking Good!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    No specific recommendations at this time. Keep up the great work!
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceTrendsDashboard;
